<template>
  <div>
    <h2
      class="text-subtitle-1 font-weight-bold text-medium-emphasis text-uppercase letter-spacing-1 mb-3"
    >
      Data types
    </h2>
    <div class="data-type-grid mb-6">
      <v-card
        v-for="key in dataTypeSelectionKeys"
        :key="key === 'all' ? 'all' : key"
        rounded="lg"
        elevation="0"
        border
        class="data-type-card"
        :variant="selectedDataType === key ? 'tonal' : 'outlined'"
        :color="selectedDataType === key ? 'primary' : undefined"
        @click="selectedDataType = key"
      >
        <v-card-text
          class="data-type-card__label text-body-2 font-weight-medium"
          :title="dataTypeCardTitle(key)"
        >
          <span class="data-type-card__label-text">{{ dataTypeCardLabel(key) }}</span>
        </v-card-text>
      </v-card>
    </div>

    <v-card rounded="lg" elevation="0" border class="sync-history-card">
      <v-card-title
        class="sync-history-card__title text-subtitle-1 font-weight-bold px-4 d-flex flex-wrap align-center justify-space-between gap-y-2 gap-x-4 w-100"
      >
        <span class="shrink-0">Sync history</span>
        <div class="sync-history-status-inline d-flex align-center justify-end min-w-0">
          <v-chip-group
            v-model="selectedStatuses"
            class="sync-history-status-chips min-w-0"
            color="primary"
            filter
            multiple
            variant="outlined"
          >
            <v-chip
              v-for="opt in statusFilterOptions"
              :key="opt.value"
              :value="opt.value"
              filter
              size="small"
              variant="outlined"
            >
              {{ opt.title }}
            </v-chip>
          </v-chip-group>
        </div>
      </v-card-title>
      <v-divider />
      <v-data-table
        v-model:page="syncHistoryPage"
        class="sync-data-table bg-transparent"
        :headers="tableData.headers"
        :items="tableData.items"
        :items-per-page="16"
        density="comfortable"
        hover
      >
        <template #item.status="{ item }">
          <v-chip size="small" variant="tonal" :color="statusChipColor(item.status)">
            {{ item.status }}
          </v-chip>
        </template>
        <template #item.message="{ item }">
          <span v-if="item.errorMessage" class="text-body-2">{{ item.errorMessage }}</span>
          <span v-else class="text-medium-emphasis">—</span>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import GoogleSearchConsoleSource from "@/sources/GoogleSearchConsole";
import { IApp } from "../../types";
import { computed, onMounted, ref, watch } from "vue";
import _ from "lodash";
import { getDataTypeLabel } from "../../utils";
import BirdEyeSource from "@/sources/BirdEye";
import GoogleAnalyticsSource from "@/sources/GoogleAnalytics";

const props = defineProps<{
  account: IApp.IGoogleSearchConsoleAccount | IApp.IGoogleAnalyticsAccount | IApp.IBirdEyeAccount;
}>();

const statusFilterOptions: { value: IApp.SyncStatus; title: string }[] = [
  { value: IApp.SyncStatus.Completed, title: "Completed" },
  { value: IApp.SyncStatus.Failed, title: "Failed" },
  { value: IApp.SyncStatus.InProgress, title: "In progress" },
  { value: IApp.SyncStatus.NotStarted, title: "Not started" }
];

const selectedStatuses = ref<IApp.SyncStatus[]>([
  IApp.SyncStatus.Completed,
  IApp.SyncStatus.Failed,
  IApp.SyncStatus.InProgress,
  IApp.SyncStatus.NotStarted
]);

function statusChipColor(status: string): "success" | "error" | "info" | "warning" | "default" {
  switch (status) {
    case IApp.SyncStatus.Completed:
      return "success";
    case IApp.SyncStatus.Failed:
      return "error";
    case IApp.SyncStatus.InProgress:
      return "info";
    case IApp.SyncStatus.NotStarted:
      return "default";
    default:
      return "default";
  }
}

const getDataTypes = () => {
  switch (props.account.feed) {
    case IApp.IFeedTypes.GoogleSearchConsole:
      return new GoogleSearchConsoleSource().getDataTypes();
    case IApp.IFeedTypes.GoogleAnalytics:
      return new GoogleAnalyticsSource().getDataTypes();
    case IApp.IFeedTypes.BirdEye:
      return new BirdEyeSource().getDataTypes();
    default:
      return [];
  }
};

const selectedDataType = ref<IApp.DataType | "all">("all");
const syncHistoryPage = ref(1);
const dataTypes = ref<IApp.DataType[]>([]);

const dataTypeSelectionKeys = computed<(IApp.DataType | "all")[]>(() => [
  "all",
  ...dataTypes.value
]);

function dataTypeCardLabel(key: IApp.DataType | "all"): string {
  return key === "all" ? "All" : getDataTypeLabel(key);
}

function dataTypeCardTitle(key: IApp.DataType | "all"): string {
  return key === "all" ? "All" : getDataTypeLabel(key);
}

const source = computed(() => {
  switch (props.account.feed) {
    case IApp.IFeedTypes.GoogleSearchConsole: {
      const ac = new GoogleSearchConsoleSource();
      ac.syncs = props.account.syncs;
      return ac;
    }
    case IApp.IFeedTypes.GoogleAnalytics: {
      const ac = new GoogleAnalyticsSource();
      ac.syncs = props.account.syncs;
      return ac;
    }
    case IApp.IFeedTypes.BirdEye: {
      const ac = new BirdEyeSource();
      ac.syncs = props.account.syncs;
      return ac;
    }
    default:
      return null;
  }
});

const syncs = computed(() => {
  if (!source.value) {
    return [];
  }
  if (selectedDataType.value === "all") {
    if (_.isEmpty(dataTypes.value)) {
      return [];
    }
    return _.sortBy(_.flatten(dataTypes.value.map((dt) => source.value!.getDataTypeSyncs(dt))), [
      "month",
      "dataType"
    ]);
  }
  return source.value.getDataTypeSyncs(selectedDataType.value);
});

const filteredSyncs = computed(() => {
  if (_.isEmpty(selectedStatuses.value)) {
    return [];
  }
  return syncs.value.filter((row) => selectedStatuses.value.includes(row.status));
});

const tableData = computed(() => {
  const showAll = selectedDataType.value === "all";
  const headers = [
    { title: "No", key: "no", width: "56px" },
    ...(showAll ? [{ title: "Data type", key: "dataTypeLabel", width: "160px" }] : []),
    { title: "Month", key: "month", width: "88px" },
    { title: "Status", key: "status", width: "120px" },
    { title: "Started at", key: "startedAt" },
    { title: "Failed at", key: "failedAt" },
    { title: "Completed at", key: "completedAt" },
    { title: "Message", key: "message" }
  ];
  const items = _(filteredSyncs.value)
    .map((item, index) => ({
      ...item,
      no: index + 1,
      dataTypeLabel: getDataTypeLabel(item.dataType)
    }))
    .value();
  return { headers, items };
});

watch(selectedDataType, () => {
  syncHistoryPage.value = 1;
});

watch(
  selectedStatuses,
  () => {
    syncHistoryPage.value = 1;
  },
  { deep: true }
);
onMounted(() => {
  dataTypes.value = getDataTypes();
});
</script>

<style scoped>
/* Data types grid: 2 / 3 / 4 / 5 cols (xs → lg+). Vuetify: sm 600, md 960, lg 1280, xl 1920 */
.data-type-grid {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

@media (min-width: 600px) {
  .data-type-grid {
    gap: 10px;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (min-width: 960px) {
  .data-type-grid {
    gap: 12px;
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

@media (min-width: 1280px) {
  .data-type-grid {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }
}

@media (min-width: 1920px) {
  .data-type-grid {
    gap: 14px;
  }
}

.data-type-card {
  cursor: pointer;
  min-width: 0;
  transition:
    box-shadow 0.15s ease,
    transform 0.15s ease;
}

.data-type-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.data-type-card :deep(.data-type-card__label.v-card-text) {
  display: flex;
  justify-content: center;
  padding: 8px 10px;
  line-height: 1.25;
  min-width: 0;
}

.data-type-card__label-text {
  display: block;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
}

@media (min-width: 600px) {
  .data-type-card :deep(.data-type-card__label.v-card-text) {
    padding: 10px 12px;
  }
}

@media (min-width: 960px) {
  .data-type-card :deep(.data-type-card__label.v-card-text) {
    padding: 12px 12px;
  }
}

.sync-history-card__title {
  min-height: unset;
  padding-block: 10px;
  line-height: 1.35;
  width: 100%;
  text-align: start;
}

.sync-history-status-inline {
  flex: 0 1 auto;
  min-width: 0;
}

@media (max-width: 599px) {
  .sync-history-status-inline {
    flex-basis: 100%;
    justify-content: flex-end;
  }
}

.sync-history-status-chips {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
}

.sync-history-status-chips :deep(.v-slide-group__content) {
  flex-wrap: nowrap;
  justify-content: flex-end;
}

.sync-history-card :deep(.v-data-table) {
  border-radius: 0 0 8px 8px;
}

.sync-data-table :deep(.v-data-table-header__content) {
  font-weight: 600;
}
</style>
